import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'i18next';
import { Link, useLocation } from 'react-router';
import { Button, Z_SIDEBAR } from '@datanomik/core-ui';
import { CloseLine } from '@datanomik/core-ui/icons';

import { navPill } from '../nav/NavPills';
import type { ShellNavEntry, ShellNavSection } from '../nav/types';

export interface MobileNavSheetProps {
  open: boolean;
  onClose: () => void;
  sections: ShellNavSection[];
  brand?: React.ReactNode;
  account?: React.ReactNode;
}

export const MobileNavSheet: React.FC<MobileNavSheetProps> = ({
  open, onClose, sections, brand, account,
}) => {
  const { pathname } = useLocation();
  const layer = useRef<HTMLDivElement>(null);

  useEffect(() => { onClose(); }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const node = layer.current;
    if (node) node.inert = !open;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (typeof document === 'undefined') return null;

  const row = (entry: ShellNavEntry, child?: { label: string; to?: string; active?: boolean }) => {
    const label = child?.label ?? entry.label;
    const to = child?.to ?? entry.to;
    const active = child?.active ?? entry.active;
    const cls = [
      'flex h-10 items-center gap-2.5 rounded-lg px-3 text-sm font-medium no-underline transition-colors',
      child ? 'pl-11 text-[13px]' : '',
      active
        ? 'bg-primary-subtle text-primary-subtle-foreground'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
    ].join(' ');

    if (to) {
      return (
        <Link key={`${entry.key}:${label}`} to={to} className={cls} onClick={onClose}>
          {!child ? entry.icon : null}
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {!child ? navPill(entry) : null}
        </Link>
      );
    }
    return (
      <button
        key={`${entry.key}:${label}`}
        type="button"
        className={cls}
        onClick={() => { entry.onSelect?.(); onClose(); }}
      >
        {!child ? entry.icon : null}
        <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      </button>
    );
  };

  return createPortal(
    <div
      ref={layer}
      data-modern
      aria-hidden={!open}
      className="fixed inset-0 font-sans"
      style={{ zIndex: Z_SIDEBAR + 10, pointerEvents: open ? 'auto' : 'none' }}
    >
      <button
        type="button"
        aria-label={t('Close menu')}
        onClick={onClose}
        className={`bg-overlay absolute inset-0 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className={`bg-surface-1 border-border absolute inset-y-0 left-0 flex w-[min(86vw,300px)] flex-col border-r shadow-depth-3 transition-transform duration-[260ms] ease-[var(--ease-emphasis)] ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="border-border flex h-14 shrink-0 items-center gap-2 border-b px-3">
          <div className="min-w-0 flex-1">{brand}</div>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('Close menu')}
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <CloseLine className="size-[18px]" />
          </Button>
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2">
          {sections.map((section) => (
            <div key={section.key} className="flex flex-col gap-0.5">
              {section.label ? (
                <span className="text-muted-foreground px-3 pt-4 pb-1 text-[10px] font-semibold tracking-[0.06em] uppercase">
                  {section.label}
                </span>
              ) : null}
              {section.entries.map((entry) => (
                <React.Fragment key={entry.key}>
                  {row(entry)}
                  {entry.children && entry.children.length > 1
                    ? entry.children.map((child) => row(entry, child))
                    : null}
                </React.Fragment>
              ))}
            </div>
          ))}
        </nav>

        <div className="border-border shrink-0 border-t p-2">{account}</div>
      </div>
    </div>,
    document.body,
  );
};

export default MobileNavSheet;
